/**
 * CssEffectOverlay.tsx
 *
 * Renders the 20 new PRO ambient effects (concept 1 + concept 2
 * v2-prefixed) as pure CSS layers. The CSS lives in
 * `./ambient-effects.css` (imported once by the consumer that mounts
 * this overlay). The overlay uses the same `code` -> CSS class map the
 * seed populates; the user's `intensity` is applied to the wrapper's
 * opacity.
 */
import { cn } from '@/lib/utils';
import './ambient-effects.css';

/** Set of codes that the CSS overlay supports. Mirrors the seed. */
export const CSS_EFFECT_CODES = new Set<string>([
  // Concept 1 (10).
  'romantic-rain', 'snowfall', 'floating-hearts', 'candle-embers', 'rose-petals',
  'golden-dust', 'aurora-sky', 'silk-waves', 'love-energy', 'neon-dust',
  'meteor-shower', 'ocean-reflection', 'cosmic-love', 'light-rays', 'liquid-gradient',
  // Concept 2 (10, v2-prefixed).
  'v2-soft-rain', 'v2-snowfall', 'v2-heart-embers', 'v2-bokeh-orbs', 'v2-aurora',
  'v2-cherry-blossoms', 'v2-starfield', 'v2-fireflies', 'v2-heartbeat-pulse',
  'v2-gradient-morph', 'v2-drifting-mist',
]);

export function CssEffectOverlay({
  code,
  intensity = 0.6,
  className,
}: {
  code: string;
  intensity?: number;
  className?: string;
}) {
  if (!CSS_EFFECT_CODES.has(code)) return null;
  return (
    <div
      role="presentation"
      aria-hidden
      className={cn('ambient-css', code, className)}
      style={{ opacity: Math.max(0, Math.min(1, intensity)) }}
    />
  );
}
